import { useEffect } from "react";
import {
  MdOutlineWifi,
  MdOutlineRefresh,
  MdOutlineNetworkCheck,
  MdOutlineRouter,
  MdSettingsEthernet,
  MdOutlineRestartAlt,
} from "react-icons/md";

import React from "react";

const DownPage = () => {
  // Auto reload when internet connection is back
  useEffect(() => {
    const handleOnline = () => {
      window.location.reload();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  return (
    <div className="min-h-screen w-full mt-4 flex items-center justify-center bg-gradient-to-br from-[#f8faff] to-[#f0f4fd] p-1 xs:p-2 sm:p-2 md:p-6">
      <div
        className="backdrop-blur-md rounded-lg sm:rounded-xl p-2 xs:px-2 sm:p-5 md:p-6 
                    w-[99%] xs:w-[90%] sm:w-[85%] md:max-w-[750px]
                    flex flex-col gap-2 xs:gap-3 sm:gap-6 relative"
      >
        {/* Icon Container */}
        <div className="text-center py-1 xs:py-2 sm:py-2">
          <div className="inline-block relative">
            <style>{`
              @keyframes float {
                0%, 100% { transform: translateY(0) rotate(-3deg); }
                50% { transform: translateY(-15px) rotate(3deg); }
              }

              @keyframes pulse-ring {
                0% { transform: scale(0.85); opacity: 0.5; }
                100% { transform: scale(1.3); opacity: 0; }
              }

              .wifi-icon {
                animation: float 3.5s ease-in-out infinite;
                transition: all 0.3s ease;
              }

              .wifi-icon:hover {
                transform: scale(1.1);
                filter: drop-shadow(0 0 10px rgba(0,0,0,0.2));
              }

              .pulse-ring {
                animation: pulse-ring 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
              }
            `}</style>

            <div className="absolute inset-0 bg-gray-100/50 rounded-full pulse-ring" />

            <MdOutlineWifi
              className="wifi-icon text-[2rem] xs:text-[2.5rem] sm:text-[3.5rem] md:text-[4rem] lg:text-[5rem]
                         text-gray-700 relative z-10 transform transition-all duration-500"
            />
          </div>
        </div>

        {/* Title */}
        <div className="text-center space-y-0.5 sm:space-y-2">
          <h3 className="text-base xs:text-lg sm:text-2xl md:text-3xl font-bold text-gray-800">
            You're offline
          </h3>
          <p className="text-[11px] xs:text-sm sm:text-base text-gray-600 max-w-md mx-auto px-1">
            It looks like you don't have an internet connection right now
          </p>
        </div>

        {/* Connection Steps */}
        <div className="w-full space-y-1.5 sm:space-y-3">
          {[
            {
              icon: <MdOutlineNetworkCheck />,
              text: "Check Wi-Fi Connection",
              desc: "Make sure Wi-Fi or mobile data is enabled",
            },
            {
              icon: <MdOutlineRouter />,
              text: "Network Settings",
              desc: "Verify your network settings are properly configured",
            },
            {
              icon: <MdSettingsEthernet />,
              text: "Internet Access",
              desc: "Ensure your device has access to the internet",
            },
            {
              icon: <MdOutlineRestartAlt />,
              text: "Try Reconnecting",
              desc: "Reconnect to your network and try again",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-1.5 xs:gap-3 sm:gap-4 bg-gray-50/80 rounded-md sm:rounded-xl p-1.5 xs:p-3 sm:p-4
                         hover:translate-x-2 transition-all duration-300 
                         hover:shadow-md hover:bg-white border border-gray-100
                         group cursor-pointer"
            >
              <div
                className="bg-white p-1 xs:p-2 sm:p-2.5 rounded-md shadow-sm 
                          group-hover:bg-green-50 group-hover:text-green-600
                          transition-all duration-300"
              >
                {React.cloneElement(item.icon, {
                  className: "text-base xs:text-[1.25rem] sm:text-[1.75rem]",
                })}
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] xs:text-sm sm:text-base font-semibold text-gray-800 group-hover:text-gray-900">
                  {item.text}
                </span>
                <span className="text-[9px] xs:text-xs sm:text-sm text-gray-500 mt-0.5 group-hover:text-gray-600">
                  {item.desc}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Button */}
        <div className="flex justify-center">
          <button
            onClick={() => window.location.reload()}
            className="bg-greenButtonColor text-white rounded-md px-3 xs:px-6 sm:px-8 py-1.5 xs:py-2.5 sm:py-3 
                     flex items-center justify-center gap-1.5 sm:gap-3 min-w-[120px] xs:min-w-[160px] sm:min-w-[180px]
                     transition-all duration-300 hover:shadow-lg 
                     hover:scale-105 active:scale-95 text-xs sm:text-base font-semibold
                     tracking-wide group"
          >
            <MdOutlineRefresh className="text-base xs:text-[1.3rem] sm:text-[1.4rem] transition-all duration-500 group-hover:animate-spin" />
            <span>Reload</span>
          </button>
        </div>

        {/* Status Indicator */}
        <div className="flex items-center justify-center gap-1 sm:gap-2">
          <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-500 rounded-full animate-pulse" />
          <span className="text-[9px] xs:text-xs text-gray-600 font-medium">
            Checking connection...
          </span>
        </div>
      </div>
    </div>
  );
};

export default DownPage;
